import React from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import type { StatutoryPlatformValues } from '../../../models/attendance';
import {
  STATUTORY_LABEL_MAX, formatStatutoryNumber,
  type StatutoryDraft, type StatutoryDraftErrors, type StatutoryValues,
} from './statutoryDraft';

/** The statutory value inputs and the read-only facts, shared by the Hr page and the Operator catalogue page. */

interface FieldsProps {
  draft: StatutoryDraft;
  errors: StatutoryDraftErrors;
  onChange: (next: StatutoryDraft) => void;
  /** Hr page only: the catalogue's values, shown under a field whose value differs from it. */
  platform?: StatutoryPlatformValues | null;
  idPrefix: string;
  disabled?: boolean;
}

/** The statutory value inputs (name, minimum, probation, carry forward, notes). */
export function StatutoryValueFields({ draft, errors, onChange, platform, idPrefix, disabled }: FieldsProps) {
  const { t } = useTranslation(['leaves', 'common']);
  const set = (patch: Partial<StatutoryDraft>) => onChange({ ...draft, ...patch });

  const platformHint = (differs: boolean, text: string) =>
    platform && differs ? t('leaves:statutoryTemplates.platformValue', { value: text }) : undefined;

  const minDiffers = platform != null && Number(draft.minDays) !== platform.minDaysPerYear;
  const probationDiffers = platform != null && Number(draft.probation) !== platform.probationMonths;
  const labelDiffers = platform != null && draft.label.trim() !== platform.leaveTypeName;
  const draftMaxCarry = draft.carryForward && draft.maxCarry.trim() !== '' ? Number(draft.maxCarry) : null;
  const carryDiffers = platform != null && (
    draft.carryForward !== platform.carryForwardAllowed
    || (draft.carryForward && draftMaxCarry !== platform.maxCarryForwardDays));
  const notesDiffers = platform != null && draft.notes.trim() !== (platform.notes ?? '').trim();

  const platformCarryText = platform
    ? (!platform.carryForwardAllowed
      ? t('leaves:statutoryTemplates.noCarryForward')
      : platform.maxCarryForwardDays == null
        ? t('leaves:statutoryTemplates.carryForwardNoCap')
        : t('leaves:statutoryTemplates.carryForwardUpTo', { days: formatStatutoryNumber(platform.maxCarryForwardDays) }))
    : '';

  return (
    <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <Input
          id={`${idPrefix}-label`}
          label={t('leaves:statutoryTemplates.statutoryName')}
          value={draft.label}
          maxLength={STATUTORY_LABEL_MAX}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ label: e.target.value })}
          error={errors.label}
          description={platformHint(labelDiffers, platform?.leaveTypeName ?? '')}
          disabled={disabled}
          data-testid={`${idPrefix}-label`}
        />
      </div>
      <Input
        id={`${idPrefix}-min-days`}
        type="number"
        inputMode="decimal"
        min={0}
        step={0.5}
        label={t('leaves:statutoryTemplates.minDaysPerYear')}
        value={draft.minDays}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ minDays: e.target.value })}
        error={errors.minDays}
        description={platformHint(minDiffers, platform
          ? formatStatutoryNumber(platform.minDaysPerYear)
          : '')}
        disabled={disabled}
        data-testid={`${idPrefix}-min-days`}
      />
      <Input
        id={`${idPrefix}-probation`}
        type="number"
        inputMode="numeric"
        min={0}
        step={1}
        label={t('leaves:statutoryTemplates.probationMonths')}
        value={draft.probation}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ probation: e.target.value })}
        error={errors.probation}
        description={platformHint(probationDiffers, platform
          ? String(platform.probationMonths)
          : '')}
        disabled={disabled}
        data-testid={`${idPrefix}-probation`}
      />
      <div className="flex flex-col gap-2 sm:col-span-2">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <Checkbox
            id={`${idPrefix}-carry`}
            label={t('leaves:statutoryTemplates.carryForwardAllowed')}
            checked={draft.carryForward}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              set({ carryForward: e.target.checked, maxCarry: e.target.checked ? draft.maxCarry : '' })}
            disabled={disabled}
            data-testid={`${idPrefix}-carry`}
          />
          {draft.carryForward && (
            <div className="w-full sm:w-48">
              <Input
                id={`${idPrefix}-max-carry`}
                type="number"
                inputMode="decimal"
                min={0}
                step={0.5}
                aria-label={t('leaves:statutoryTemplates.maxCarryDays')}
                placeholder={t('leaves:statutoryTemplates.noCapPlaceholder')}
                value={draft.maxCarry}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ maxCarry: e.target.value })}
                error={errors.maxCarry}
                disabled={disabled}
                data-testid={`${idPrefix}-max-carry`}
              />
            </div>
          )}
        </div>
        {platform && carryDiffers && (
          <p className="text-xs text-muted-foreground">{t('leaves:statutoryTemplates.platformValue', { value: platformCarryText })}</p>
        )}
      </div>
      <div className="flex flex-col gap-1.5 sm:col-span-2">
        <label htmlFor={`${idPrefix}-notes`} className="text-sm font-medium text-foreground">
          {t('leaves:statutoryTemplates.notes')}
        </label>
        <textarea
          id={`${idPrefix}-notes`}
          value={draft.notes}
          onChange={e => set({ notes: e.target.value })}
          rows={2}
          disabled={disabled}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary disabled:opacity-60"
          data-testid={`${idPrefix}-notes`}
        />
        {platform && notesDiffers && (
          <p className="text-xs text-muted-foreground">
            {platform.notes
              ? t('leaves:statutoryTemplates.platformValue', { value: platform.notes })
              : t('leaves:statutoryTemplates.platformNoNotes')}
          </p>
        )}
      </div>
    </div>
  );
}

interface FactsProps {
  values: StatutoryValues;
  /** Hr page: a chip "Platform: N" beside each value that differs from the catalogue. */
  platform?: StatutoryPlatformValues | null;
  testIdPrefix: string;
}

function PlatformChip({ text, testId }: { text: string; testId: string }) {
  return (
    <span
      className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-normal text-muted-foreground"
      data-testid={testId}
    >
      {text}
    </span>
  );
}

/** The three values of a row, read-only, as a short definition list. */
export function StatutoryFacts({ values, platform, testIdPrefix }: FactsProps) {
  const { t } = useTranslation(['leaves', 'common']);
  const daysText = (n: number) => formatStatutoryNumber(n);
  const carryText = (allowed: boolean, cap: number | null) => (!allowed
    ? t('leaves:statutoryTemplates.noCarryForward')
    : cap == null
      ? t('leaves:statutoryTemplates.carryForwardNoCap')
      : t('leaves:statutoryTemplates.carryForwardUpTo', { days: formatStatutoryNumber(cap) }));
  const monthsText = (n: number) => (n > 0
    ? String(n)
    : t('leaves:statutoryTemplates.noProbation'));

  const minDiffers = platform != null && platform.minDaysPerYear !== values.minDaysPerYear;
  const carryDiffers = platform != null && (platform.carryForwardAllowed !== values.carryForwardAllowed
    || platform.maxCarryForwardDays !== values.maxCarryForwardDays);
  const probationDiffers = platform != null && platform.probationMonths !== values.probationMonths;

  return (
    <dl className="flex flex-wrap gap-x-8 gap-y-3">
      <div className="min-w-0">
        <dt className="text-xs text-muted-foreground">{t('leaves:statutoryTemplates.minDaysPerYear')}</dt>
        <dd className="mt-0.5 flex flex-wrap items-center gap-2 text-sm font-medium tabular-nums text-foreground">
          <span data-testid={`${testIdPrefix}-min-days`}>{daysText(values.minDaysPerYear)}</span>
          {platform && minDiffers && (
            <PlatformChip
              testId={`${testIdPrefix}-platform-min-days`}
              text={t('leaves:statutoryTemplates.platformChip', { value: daysText(platform.minDaysPerYear) })}
            />
          )}
        </dd>
      </div>
      <div className="min-w-0">
        <dt className="text-xs text-muted-foreground">{t('leaves:statutoryTemplates.carryForwardDays')}</dt>
        <dd className="mt-0.5 flex flex-wrap items-center gap-2 text-sm font-medium tabular-nums text-foreground">
          <span>{carryText(values.carryForwardAllowed, values.maxCarryForwardDays)}</span>
          {platform && carryDiffers && (
            <PlatformChip
              testId={`${testIdPrefix}-platform-carry`}
              text={t('leaves:statutoryTemplates.platformChip', { value: carryText(platform.carryForwardAllowed, platform.maxCarryForwardDays) })}
            />
          )}
        </dd>
      </div>
      <div className="min-w-0">
        <dt className="text-xs text-muted-foreground">{t('leaves:statutoryTemplates.probationMonths')}</dt>
        <dd className="mt-0.5 flex flex-wrap items-center gap-2 text-sm font-medium tabular-nums text-foreground">
          <span>{monthsText(values.probationMonths)}</span>
          {platform && probationDiffers && (
            <PlatformChip
              testId={`${testIdPrefix}-platform-probation`}
              text={t('leaves:statutoryTemplates.platformChip', { value: monthsText(platform.probationMonths) })}
            />
          )}
        </dd>
      </div>
    </dl>
  );
}
